import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { ThumbsDown, Warning, ChatCircle } from "@phosphor-icons/react";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const sec = (Date.now() - d.getTime()) / 1000;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
}

function TriageRow({ t, kind }) {
    return (
        <div
            data-testid={`triage-${kind}-${t.id}`}
            className="border border-white/10 rounded-sm p-4 bg-slate-900/40 hover:bg-slate-900/60 transition"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500">{t.category || "Task"}</span>
                        {kind === "negative" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/30">
                                Negative rating
                            </span>
                        )}
                        {kind === "stale" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                No rating · {timeAgo(t.updated_at)}
                            </span>
                        )}
                    </div>
                    <div className="font-serif text-base text-white">{t.title || "Untitled job"}</div>
                    <div className="text-xs text-slate-400 mt-1">
                        Client: <span className="text-slate-200">{t.client_name || t.client_email}</span> &nbsp;·&nbsp;
                        Amount: <span className="gold-text">${(t.final_amount ?? t.ai_quote_amount)?.toFixed?.(0) || "—"}</span>
                    </div>
                    {t.contractor && (
                        <div className="text-xs text-slate-400 mt-1">
                            Contractor: <span className="text-slate-200">{t.contractor.name}</span>
                            {t.contractor.phone && ` · ${t.contractor.phone}`}
                            {t.contractor.email && ` · ${t.contractor.email}`}
                        </div>
                    )}
                    {kind === "negative" && t.client_rating_comment && (
                        <p className="mt-2 text-sm text-slate-300 border-l-2 border-red-400/40 pl-3 italic">
                            "{t.client_rating_comment}"
                        </p>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    <a
                        href={`mailto:${t.client_email}`}
                        data-testid={`triage-email-client-${t.id}`}
                        className="text-xs px-3 py-1.5 rounded-sm border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 inline-flex items-center gap-1"
                    >
                        <ChatCircle size={12} weight="fill" /> Email client
                    </a>
                    <Link
                        to={`/tasks/${t.id}`}
                        data-testid={`triage-open-${t.id}`}
                        className="text-xs px-3 py-1.5 rounded-sm border border-gold/40 bg-gold/10 text-gold hover:bg-gold/20"
                    >
                        Open task →
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function TriagePanel() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await http.get("/admin/triage");
            setData(data);
        } catch (e) { setErr(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="text-slate-500 text-sm py-4">Loading triage queue…</div>;

    const neg = data.negative || [];
    const stale = data.stale || [];

    return (
        <div data-testid="admin-triage-panel" className="space-y-6 mb-12">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-gold mb-1">/ Triage queue</div>
                    <h2 className="font-serif text-3xl">Needs <span className="italic gold-text">attention</span></h2>
                </div>
                <div className="flex gap-3 text-xs">
                    <span className="px-3 py-1.5 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 inline-flex items-center gap-1.5">
                        <ThumbsDown size={12} weight="fill" /> {neg.length} unhappy
                    </span>
                    <span className="px-3 py-1.5 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-300 inline-flex items-center gap-1.5">
                        <Warning size={12} weight="fill" /> {stale.length} stale
                    </span>
                </div>
            </div>

            {err && <div className="text-red-400 text-sm">{err}</div>}

            {neg.length === 0 && stale.length === 0 && (
                <div data-testid="triage-empty" className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 p-5 text-sm text-emerald-200">
                    All clear. No negative ratings and no stale paid jobs in the last 7 days.
                </div>
            )}

            {neg.length > 0 && (
                <section data-testid="triage-negative-list">
                    <h3 className="text-sm uppercase tracking-widest text-slate-400 mb-3">Negative ratings</h3>
                    <div className="space-y-2">
                        {neg.map((t) => <TriageRow key={t.id} t={t} kind="negative" />)}
                    </div>
                </section>
            )}

            {stale.length > 0 && (
                <section data-testid="triage-stale-list">
                    <h3 className="text-sm uppercase tracking-widest text-slate-400 mb-3">Paid · no rating yet ({"> 7 days"})</h3>
                    <div className="space-y-2">
                        {stale.map((t) => <TriageRow key={t.id} t={t} kind="stale" />)}
                    </div>
                </section>
            )}
        </div>
    );
}
